import { Button, Flex, Typography } from 'antd';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useApiFeedback } from '@/hooks/useApiFeedback';
import { SPACE_MD, SPACE_SM } from '@/theme/buildAntdTheme';

const COPIED_LABEL_RESET_MS = 2000;

const formatBackupCodesText = (
  codes: string[],
  title: string,
  intro: string,
): string => [title, '', intro, '', ...codes].join('\n');

interface MFABackupCodesPanelProps {
  codes: string[];
}

/** Renders backup codes with copy and download actions, shared by both enrolment paths. */
const MFABackupCodesPanel = ({ codes }: MFABackupCodesPanelProps) => {
  const [t] = useTranslation();
  const { showSuccess, showError } = useApiFeedback();
  const [copied, setCopied] = useState(false);
  const copiedTimerRef = useRef<number>(undefined);

  useEffect(
    () => () => {
      window.clearTimeout(copiedTimerRef.current);
    },
    [],
  );

  const buildText = () =>
    formatBackupCodesText(
      codes,
      t('MFA_BACKUP_CODES_FILE_TITLE'),
      t('MFA_BACKUP_CODES_FILE_INTRO'),
    );

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(buildText());
    } catch {
      showError('MFA_BACKUP_CODES_COPY_FAILED');
      return;
    }
    setCopied(true);
    showSuccess('MFA_BACKUP_CODES_COPIED');
    window.clearTimeout(copiedTimerRef.current);
    copiedTimerRef.current = window.setTimeout(
      () => setCopied(false),
      COPIED_LABEL_RESET_MS,
    );
  };

  const handleDownload = () => {
    const url = URL.createObjectURL(
      new Blob([buildText()], { type: 'text/plain;charset=utf-8' }),
    );
    const link = document.createElement('a');
    link.href = url;
    link.download = t('MFA_BACKUP_CODES_FILENAME');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    showSuccess('MFA_BACKUP_CODES_DOWNLOADED');
  };

  return (
    <Flex vertical gap={SPACE_MD}>
      <Flex wrap gap={SPACE_SM}>
        {codes.map((code) => (
          <Typography.Text key={code} code>
            {code}
          </Typography.Text>
        ))}
      </Flex>
      <Flex wrap gap={SPACE_SM}>
        <Button onClick={() => void handleCopy()}>
          {copied
            ? t('MFA_BACKUP_CODES_COPY_DONE')
            : t('MFA_BACKUP_CODES_COPY')}
        </Button>
        <Button onClick={handleDownload}>
          {t('MFA_BACKUP_CODES_DOWNLOAD')}
        </Button>
      </Flex>
    </Flex>
  );
};

export default MFABackupCodesPanel;
